import { readErrorMessage } from "../../utils/apiError";
import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../utils/constants";
import { useAuth } from "../../components/AuthProvider";
import ClearForm from "../../components/Clearform";
import ReusableTable from "../../components/ReusableTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import { authUrl } from "../../utils/auth";

// Error Boundary component for PDF display
class PDFErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, blobUrl: props.blobUrl };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PDF Error Boundary caught an error:', error, errorInfo);
  }

  static getDerivedStateFromProps(props, state) {
    // Update blobUrl in state when prop changes
    if (props.blobUrl !== state.blobUrl) {
      // Clean up old blob URL if it exists and is different
      if (state.blobUrl && props.blobUrl !== state.blobUrl) {
        URL.revokeObjectURL(state.blobUrl);
      }
      return { blobUrl: props.blobUrl };
    }
    return null;
  }

  handleRetry = () => {
    // Clean up existing blob URL from state
    if (this.state.blobUrl) {
      URL.revokeObjectURL(this.state.blobUrl);
    }
    this.setState({ hasError: false, error: null });
    // Call the retry callback if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #dc3545',
          borderRadius: '5px',
          backgroundColor: '#f8d7da',
          color: '#721c24'
        }}>
          <h5>PDF Display Error</h5>
          <p>Unable to display the PDF document. Please try generating the award again.</p>
          <button
            className="btn btn-danger"
            onClick={this.handleRetry}
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const GenerateAward = () => {
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const [excelData, setExcelData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifiedData, setVerifiedData] = useState(false);
  const [errorExcel, setErrorExcel] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [save, setSave] = useState(false);
  const [showPDF, setShowPDF] = useState(false);
  const [upload, setUpload] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  // The award is made one lot at a time (lot no + bank + product); the Excel must hold one lot only
  const [selectedValues, setSelectedValues] = useState({
    lotNo: null,
    clientID: null,
    productID: null,
    lotLabel: "",
  });
  const [blobUrl, setBlobUrl] = useState(null);
  const { user, logout } = useAuth();
  const [arbId, setArbId] = useState("");
  const [clearForm, setClearForm] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [certifiedPdfFile, setCertifiedPdfFile] = useState(null);
  const [pageInterval, setPageInterval] = useState('');
  const [certifiedDate, setCertifiedDate] = useState('');
  // Which case gets which pages of the certified PDF, and the result message
  const [certifiedPlan, setCertifiedPlan] = useState(null);
  // Step 1: the lot this award run is for (its sample Excel, check, award and certified upload)
  const [awardLots, setAwardLots] = useState(null);
  const [chosenLotKey, setChosenLotKey] = useState("");
  const lotKeyOf = (l) => `${l.lotNo}|${l.clientId}|${l.productId}|${l.step}`;
  const [doneMessage, setDoneMessage] = useState("");

  const itemsPerPage = 10; // change as needed

  // State for pagination
  const [currentPage1, setCurrentPage1] = useState(1);

  // Calculate indexes
  const indexOfLastItem = currentPage1 * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // ✅ Paginated extracted data from Excel Data
  const currentItems1 = excelData.slice(indexOfFirstItem, indexOfLastItem);

  // ✅ Page numbers based on total data length
  const pageNumbers1 = [];
  for (let i = 1; i <= Math.ceil(excelData.length / itemsPerPage); i++) {
    pageNumbers1.push(i);
  }

  // Lots of this arbitrator with cases ready for an award
  useEffect(() => {
    if (!arbId) return;
    fetch(`${API_BASE_URL}/api/AwardLots?Arb_id=${arbId}`)
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) throw new Error(body.message || res.statusText);
        setAwardLots(body);
      })
      .catch((e) => setError(`Could not load the lots: ${e.message}`));
  }, [arbId]);

  // Load which case gets which pages once the certified upload step is reached
  useEffect(() => {
    if (!uploadDone || selectedValues.lotNo == null || !arbId) return;
    const params = new URLSearchParams({
      Lot_no: selectedValues.lotNo,
      Client_id: selectedValues.clientID,
      Product_id: selectedValues.productID,
      Arb_id: arbId,
    });
    fetch(`${API_BASE_URL}/api/CertifiedAwardPlan?${params.toString()}`)
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) throw new Error(body.message || res.statusText);
        setCertifiedPlan(body);
      })
      .catch((e) => setError(`Could not load the cases of this lot: ${e.message}`));
  }, [uploadDone, selectedValues, arbId]);

  useEffect(() => {
    // console.log(user);
    if (user && user[0]) {
      const refId = user[0].Ref_id;
      // Validate that arbId is a valid number
      if (refId && !isNaN(refId) && Number(refId) > 0) {
        setArbId(refId.toString());
      } else {
        console.error("Invalid arbId format:", refId);
        setError("Invalid arbitrator ID format. Please contact support.");
      }
    }
  }, [user]);

  // Cleanup blob URL when PDF is closed/hidden or when component unmounts
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    };
  }, [blobUrl]);

  // Separate effect for PDF visibility changes
  useEffect(() => {
    if (!showPDF && pdfUrl && blobUrl) {
      // Only cleanup if PDF is intentionally hidden, not just re-rendering
      const timer = setTimeout(() => {
        if (!showPDF) { // Double-check after timeout
          URL.revokeObjectURL(blobUrl);
          setBlobUrl(null);
          setPdfUrl("");
        }
      }, 100); // Small delay to avoid rapid cleanup
      return () => clearTimeout(timer);
    }
  }, [showPDF, blobUrl, pdfUrl]);

  // console.log(arbId);

  // For the customStepper starts Here
  const steps = [
    "Upload Excel",
    "Verify",
    "Save Award",
    "Preview PDF",
    "Upload Certified",
  ];

  // Function to move to a specific step in Stepper Component
  const handleStepChange = (step) => {
    setActiveStep(step);
  };
  // For the customStepper ends Here

  const requiredColumns = [
    "first_Hearing_Traking_report",
    "second_Hearing_Traking_report",
    "third_Hearing_Traking_report",
    "Exparty",
    "Evidence",
    "Argument",
    "Award_Date",
    "Award_full_date",
    "Clause_No",
  ];

  // Choosing a lot jumps to the step it is at: award Excel, award PDF, or certified copy
  const chooseLot = (key) => {
    setChosenLotKey(key);
    setError("");
    const lot = awardLots?.find((l) => lotKeyOf(l) === key);
    if (!lot) return;
    const values = { lotNo: lot.lotNo, clientID: lot.clientId, productID: lot.productId, lotLabel: lot.label };
    if (lot.step === "saved") {
      setSelectedValues(values);
      setVerifiedData(true);
      setSave(true);
    } else if (lot.step === "uploaded") {
      setSelectedValues(values);
      setVerifiedData(true);
      setSave(true);
      setUploadDone(true);
    }
  };

  const handleDownloadSampleExcel = () => {
    if (!arbId) { alert("Arbitrator ID not loaded yet. Please wait."); return; }
    const lot = awardLots?.find((l) => lotKeyOf(l) === chosenLotKey);
    if (!lot) { alert("Choose the lot first."); return; }
    const params = new URLSearchParams({ Arb_id: arbId, Lot_no: lot.lotNo, Client_id: lot.clientId, Product_id: lot.productId });
    window.open(authUrl(`${API_BASE_URL}/api/GenerateAwardExcel?${params.toString()}`), '_blank');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setError("");
    setFileName("");

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // ✅ Convert sheet to JSON (first row = headers)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (jsonData.length === 0) {
        setError("Error: Excel file is empty.");
        return;
      }

      // ✅ Extract headers from the first row
      const headers = Object.keys(jsonData[0]);

      // ✅ Check if all required columns exist
      const missingColumns = requiredColumns.filter(
        (col) => !headers.includes(col)
      );

      if (missingColumns.length > 0) {
        setError(
          `Error: Missing required columns - ${missingColumns.join(", ")}`
        );
        setExcelData([]);
        return;
      }

      // ✅ Check for empty values in required columns
      const emptyCells = [];

      jsonData.forEach((row, rowIndex) => {
        requiredColumns.forEach((col) => {
          if (row[col] === "" || row[col] === null || row[col] === undefined) {
            emptyCells.push(`Row ${rowIndex + 2} - ${col}`); // +2 because Excel headers start at row 1
          }
        });
      });

      if (emptyCells.length > 0) {
        setError(
          `Error: The following required cells are empty:\n${emptyCells.join(
            "\n"
          )}`
        );
        setExcelData([]);
        return;
      }

      // ✅ If everything is valid
      setExcelData(jsonData);
      setFileName(file.name);
      setError("");
      alert("✅ File validated successfully!");
    };

    reader.readAsArrayBuffer(file);
  };

  // console.log(excelData[0].REFERENCE_NO);

  if (loading) return <LoadingSpinner />;

  const handleVerify = async () => {
    try {
      setLoading(true);

      let errorRows = [];
      let allSuccess = true;

      const lots = new Map(); // lot key → { label, count }

      for (const row of excelData) {
        const referenceNo = row.REFERENCE_NO;

        const response = await fetch(`${API_BASE_URL}/api/ValidateExcel`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ Reference_no: referenceNo }),
        });

        if (!response.ok) {
          allSuccess = false;

          let errorText = "";
          try {
            const err = await response.json();
            errorText = err.message || JSON.stringify(err);
          } catch {
            errorText = "Unknown error";
          }

          errorRows.push({
            REFERENCE_NO: referenceNo,
            ERROR_MESSAGE: errorText,
          });
        } else {
          const result = await response.json();
          row.Lot_no = result[0].lotNo;
          row.Client_id = result[0].clientId;
          row.Product_id = result[0].productId;
          const lotKey = `${result[0].lotNo}|${result[0].clientId}|${result[0].productId}`;
          const lot = lots.get(lotKey) || { label: result[0].lotLabel, count: 0, refs: [] };
          lot.count += 1;
          lot.refs.push(referenceNo);
          lots.set(lotKey, lot);
        }
      }

      // Every row must be of the chosen lot: preview, award upload and certified upload all work on that lot
      const chosen = awardLots?.find((l) => lotKeyOf(l) === chosenLotKey);
      const chosenLot = chosen ? `${chosen.lotNo}|${chosen.clientId}|${chosen.productId}` : "";
      const otherLots = [...lots.entries()].filter(([key]) => key !== chosenLot);
      if (otherLots.length) {
        const list = otherLots.map(([, l]) => `• ${l.label}: ${l.refs.join(", ")}`).join("\n");
        setVerifiedData(false);
        setError(
          `You chose ${chosen?.label}, but this Excel also has cases of other lots:\n${list}\n\n` +
            "Remove those rows (or choose their lot instead). Use \"Download Sample Excel\" to get this lot's cases."
        );
        return;
      }

      // The run is for the chosen lot
      if (chosen) {
        setSelectedValues({ lotNo: chosen.lotNo, clientID: chosen.clientId, productID: chosen.productId, lotLabel: chosen.label });
      }

      if (!allSuccess) {
        const ws = XLSX.utils.json_to_sheet(errorRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Errors");

        XLSX.writeFile(wb, "ValidationErrors.xlsx");

        setVerifiedData(false);
        setErrorExcel(true);
        alert("Validation completed with errors. Error Excel downloaded ✅");
      } else {
        setVerifiedData(true);
        alert("All Reference Numbers Verified Successfully ✅");
      }
    } catch (error) {
      console.error("Network / Server Issue:", error);
      setVerifiedData(false);
    } finally {
      setLoading(false);
    }
  };

  // console.log(selectedLotNo);
  // console.log(selectedClientID);
  // console.log(selectedProductID);

  const handleSave = async () => {
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      setLoading(true);
      setError(""); // Clear previous errors

      for (const row of excelData) {
        const referenceNo = row.REFERENCE_NO;

        const DataToSend = {
          Reference_no: row.REFERENCE_NO,
          Award_date: row.Award_Date,
          Award_full_date: row.Award_full_date,
          M_1st_Hearing_Traking_report: row.first_Hearing_Traking_report,
          M_2nd_Hearing_Traking_report: row.second_Hearing_Traking_report,
          M_3rd_Hearing_Traking_report: row.third_Hearing_Traking_report,
          Exparty: row.Exparty,
          Evidence: row.Evidence,
          Argument: row.Argument,
          Clause_No: row.Clause_No,
        };

        try {
          const response = await fetch(`${API_BASE_URL}/api/AwardData`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(DataToSend),
          });

          if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.message || `HTTP ${response.status}`;
            errors.push({
              REFERENCE_NO: referenceNo,
              ERROR_MESSAGE: errorMessage
            });
            errorCount++;
            console.error(`Error for REFERENCE_NO ${referenceNo}:`, errorData);
          } else {
            const result = await response.json();
            successCount++;
          }
        } catch (fetchError) {
          errors.push({
            REFERENCE_NO: referenceNo,
            ERROR_MESSAGE: `Network error: ${fetchError.message}`
          });
          errorCount++;
          console.error(`Network error for REFERENCE_NO ${referenceNo}:`, fetchError);
        }
      }

      if (errorCount > 0) {
        // Create error report
        const ws = XLSX.utils.json_to_sheet(errors);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Save Errors");
        XLSX.writeFile(wb, "AwardSaveErrors.xlsx");

        setError(`Failed to save ${errorCount} records. Error report downloaded. ${successCount} succeeded.`);
        setSave(false);
      } else {
        setSave(true);
        setError(""); // Clear error state on success
        alert(`All ${successCount} award records saved successfully!`);
      }
    } catch (error) {
      console.error("Unexpected error in handleSave:", error);
      setError(`Unexpected error: ${error.message}`);
      setSave(false);
    } finally {
      setLoading(false);
    }
  };

  // for the generation of dearft function starts
  const handleGenerateAward = async () => {
    setLoading(true); // Start loading before fetching data
    try {
      // Validate arbId before making API call
      if (!arbId || isNaN(arbId) || Number(arbId) <= 0) {
        throw new Error("Invalid arbitrator ID. Please refresh the page and try again.");
      }

      // Fetch the PDF file from the API
      const params = new URLSearchParams();
      if (selectedValues.lotNo != null) params.append('Lot_no', selectedValues.lotNo);
      if (selectedValues.clientID != null) params.append('Client_id', selectedValues.clientID);
      if (selectedValues.productID != null) params.append('Product_id', selectedValues.productID);
      if (arbId) params.append('Arb_id', arbId);

      const response = await fetch(
        `${API_BASE_URL}/api/AwardCombinedLetter?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      // Convert the response to a Blob
      const pdfBlob = await response.blob();
      // Create a URL for the Blob
      const pdfUrl1 = URL.createObjectURL(pdfBlob);
      // Set the PDF URL to the state
      setPdfUrl(pdfUrl1);
      setBlobUrl(pdfUrl1); // Store for cleanup
      setShowPDF(true);
      setUpload(true);
      handleStepChange(4);
      setError(""); // Clear any previous errors on success
    } catch (error) {
      console.error("Error fetching and displaying the PDF:", error);
      setError(`Failed to generate award PDF: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  // for the generation of dearft function ends

  const handleUploadAward = async () => {
    setLoading(true);
    try {
      if (!arbId || isNaN(arbId) || Number(arbId) <= 0) {
        throw new Error("Invalid arbitrator ID. Please refresh the page and try again.");
      }

      const params = new URLSearchParams();
      if (selectedValues.lotNo != null) params.append('Lot_no', selectedValues.lotNo);
      if (selectedValues.clientID != null) params.append('Client_id', selectedValues.clientID);
      if (selectedValues.productID != null) params.append('Product_id', selectedValues.productID);
      if (arbId) params.append('Arb_id', arbId);

      const response = await fetch(
        `${API_BASE_URL}/api/UploadAward?${params.toString()}`,
        { method: 'POST' }
      );
      if (!response.ok) {
        throw new Error(`Failed to upload award: ${response.status} ${response.statusText}`);
      }
      setShowPDF(false);
      setUploadDone(true);
      setError("");
    } catch (error) {
      console.error("Error in Uploading Award:", error);
      setError(`Failed to upload award: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCertifiedUpload = async () => {
    if (!certifiedPdfFile) { setError('Please select the certified PDF file'); return; }
    if (!pageInterval || Number(pageInterval) <= 0) { setError('Please enter a valid page interval'); return; }

    const params = new URLSearchParams();
    if (selectedValues.lotNo != null) params.append('Lot_no', selectedValues.lotNo);
    if (selectedValues.clientID != null) params.append('Client_id', selectedValues.clientID);
    if (selectedValues.productID != null) params.append('Product_id', selectedValues.productID);
    if (arbId) params.append('Arb_id', arbId);
    params.append('PageInterval', pageInterval);
    if (certifiedDate) params.append('Certified_date', certifiedDate);

    const formData = new FormData();
    formData.append('file', certifiedPdfFile);

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/UploadCertifiedAward?${params.toString()}`,
        { method: 'POST', body: formData }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `${response.status} ${response.statusText}`);
      }
      const result = await response.json().catch(() => ({}));
      setDoneMessage(result.message || "");
      setClearForm(true);
      setError('');
    } catch (error) {
      console.error('Error uploading certified award:', error);
      setError(`Failed to upload certified award: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="row align-items-center ">
        <div className="col-md-5">
          {/* {showTable && !showData && !showPDF && !clearForm && (
            <h5>Generate Acceptance Letter</h5>
          )}
          {showPDF && !clearForm && <h5>Upload Acceptance Letter</h5>} */}
          {!fileName && !verifiedData && (
            <div className="row">
              <h4>Generate Award</h4>
            </div>
          )}
          {showPDF && !clearForm && <h5>Preview Combined Award PDF</h5>}
          {uploadDone && !clearForm && <h5>Upload Certified Award</h5>}
        </div>

        <div className="col-md-5"></div>

        <div className="col-md-2">
          {fileName && !verifiedData && (
            <button
              className="custBtn"
              style={{ fontSize: "12px" }}
              onClick={handleVerify}
            >
              Verify
            </button>
          )}

          {verifiedData && !save && (
            <button className="custBtn" onClick={handleSave}>
              Save Award
            </button>
          )}

          {verifiedData && save && !showPDF && !uploadDone && (
            <button className="custBtn" onClick={handleGenerateAward}>
              Preview Award PDF
            </button>
          )}

          {showPDF && !clearForm && (
            <button className="custBtn" onClick={handleUploadAward}>
              Upload Award
            </button>
          )}

        </div>
      </div>

      {!fileName && !verifiedData && (
        <div className="row pt-2">
          <div className="col-md-12 mb-2" style={{ fontSize: "13px" }}>
            <label className="form-label mb-1 fw-semibold">1. Choose the lot</label>
            {awardLots === null ? (
              <div className="text-muted">Loading lots…</div>
            ) : awardLots.length === 0 ? (
              <div className="text-muted">
                No lot has an award step to do: every awarded case has its certified copy. A case becomes ready for its award
                once its 3rd hearing, SOC, Sec 17 application and Sec 17 order are done.
              </div>
            ) : (
              <Form.Select
                value={chosenLotKey}
                onChange={(e) => chooseLot(e.target.value)}
                style={{ fontSize: "13px", maxWidth: "520px" }}
              >
                <option value="">— choose a lot —</option>
                {[
                  ["ready", "Ready for the award – upload the award Excel"],
                  ["saved", "Award saved – preview and upload the award PDF"],
                  ["uploaded", "Award uploaded – upload the certified copy"],
                ].map(([step, title]) =>
                  awardLots.some((l) => l.step === step) ? (
                    <optgroup key={step} label={title}>
                      {awardLots
                        .filter((l) => l.step === step)
                        .map((l) => (
                          <option key={lotKeyOf(l)} value={lotKeyOf(l)}>
                            {l.label} ({l.caseCount} case{l.caseCount === 1 ? "" : "s"})
                          </option>
                        ))}
                    </optgroup>
                  ) : null,
                )}
              </Form.Select>
            )}
            {chosenLotKey.endsWith("|ready") && (
              <div className="text-muted mt-1">
                2. Download this lot's Excel, fill in the award details, then choose it below. The award, its preview and
                the certified upload are all for this lot only.
              </div>
            )}
          </div>
          {chosenLotKey.endsWith("|ready") && (
          <>
          <div className="col-md-3">
            <Form.Group>
              <Form.Control
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileUpload}
                className="custom-input"
                style={{ fontSize: "12px" }}
              />
            </Form.Group>
          </div>
          <div className="col-md-2">
            <button
              className="custBtn"
              style={{ fontSize: "12px" }}
              onClick={handleDownloadSampleExcel}
            >
              ⬇ Download Sample Excel
            </button>
          </div>
          </>
          )}
          <div className="col-md-5">
            {error && (
              <div
                style={{
                  color: "red",
                  background: "#ffe6e6",
                  padding: "10px",
                  borderRadius: "5px",
                }}
              >
                {error}
              </div>
            )}
          </div>
        </div>
      )}

      {fileName && !verifiedData && error && (
        <div className="mt-2 mb-2 p-2 rounded" style={{ color: "#842029", background: "#f8d7da", fontSize: "13px", whiteSpace: "pre-line" }}>
          {error}
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => {
                setError("");
                setFileName("");
                setExcelData([]);
              }}
            >
              Choose another Excel
            </button>
          </div>
        </div>
      )}

      {/* Which lot this award run is for */}
      {verifiedData && selectedValues.lotLabel && !clearForm && (
        <div className="mt-2 mb-2 p-2 rounded" style={{ background: "#e7f1ff", fontSize: "13px" }}>
          Lot: <strong>{selectedValues.lotLabel}</strong>
          <button type="button" className="btn btn-link btn-sm p-0 ms-3 align-baseline" onClick={() => window.location.reload()}>
            Choose another lot
          </button>
        </div>
      )}

      {fileName && !verifiedData && !save && !errorExcel && (
        <ReusableTable
          data={currentItems1}
          currentPage={currentPage1}
          pageNumbers={pageNumbers1}
          setCurrentPage={setCurrentPage1}
        />
      )}

      {verifiedData && !save && (
        <ReusableTable
          data={currentItems1}
          currentPage={currentPage1}
          pageNumbers={pageNumbers1}
          setCurrentPage={setCurrentPage1}
        />
      )}

      {verifiedData && save && !showPDF && (
        <ReusableTable
          data={currentItems1}
          currentPage={currentPage1}
          pageNumbers={pageNumbers1}
          setCurrentPage={setCurrentPage1}
        />
      )}

      {showPDF && !clearForm && (
        <div className="row mt-3">
          <div className="col-md-12">
            <PDFErrorBoundary onRetry={handleGenerateAward} blobUrl={blobUrl}>
              <iframe
                src={pdfUrl}
                style={{ width: "100%", height: "100vh" }}
                title="PDF Viewer"
                onError={(e) => {
                  console.error('PDF iframe loading error:', e);
                  setError('Failed to load PDF document. Please try generating again.');
                }}
              />
            </PDFErrorBoundary>
          </div>
        </div>
      )}

      {uploadDone && !clearForm && (
        <div className="row pt-3 align-items-end">
          <h5 className="mb-1">Upload Certified Award PDF</h5>
          {certifiedPlan && (
            <div className="col-md-12 mb-3" style={{ fontSize: "12px" }}>
              <div className="p-2 rounded mb-2" style={{ background: "#e7f1ff" }}>
                This upload is for <strong>{certifiedPlan.lotLabel}</strong> only ({certifiedPlan.cases.length} case
                {certifiedPlan.cases.length === 1 ? "" : "s"}). Upload one PDF with the certified award of every case
                below, in this order.
                {Number(pageInterval) > 0 && (
                  <>
                    {" "}
                    Your PDF must have exactly <strong>{certifiedPlan.cases.length * Number(pageInterval)} pages</strong> (
                    {certifiedPlan.cases.length} cases × {pageInterval} page{Number(pageInterval) === 1 ? "" : "s"} each).
                  </>
                )}
              </div>
              <table className="table table-sm table-bordered mb-0" style={{ maxWidth: "720px" }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "110px" }}>PDF pages</th>
                    <th>Case</th>
                    <th>Borrower</th>
                  </tr>
                </thead>
                <tbody>
                  {certifiedPlan.cases.map((c, i) => {
                    const per = Number(pageInterval) > 0 ? Number(pageInterval) : null;
                    const pages = per ? (per === 1 ? `${i + 1}` : `${i * per + 1} – ${(i + 1) * per}`) : "—";
                    return (
                      <tr key={c.referenceNo}>
                        <td>{pages}</td>
                        <td>
                          {c.referenceNo}
                          {c.alreadyCertified && <span className="text-muted"> (already uploaded – will be replaced)</span>}
                        </td>
                        <td>{c.borrowerName}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="col-md-3">
            <label className="form-label" style={{ fontSize: "12px" }}>
              Certified PDF File
            </label>
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={(e) => setCertifiedPdfFile(e.target.files[0] || null)}
              className="custom-input"
              style={{ fontSize: "12px" }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label" style={{ fontSize: "12px" }}>
              Pages per Case
            </label>
            <Form.Control
              type="number"
              min="1"
              placeholder="e.g. 2"
              value={pageInterval}
              onChange={(e) => setPageInterval(e.target.value)}
              className="custom-input"
              style={{ fontSize: "12px" }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label" style={{ fontSize: "12px" }}>
              Certified Date (optional)
            </label>
            <Form.Control
              type="date"
              value={certifiedDate}
              onChange={(e) => setCertifiedDate(e.target.value)}
              className="custom-input"
              style={{ fontSize: "12px" }}
            />
          </div>
          {/* always shown next to its fields; disabled until the PDF and pages per case are entered */}
          <div className="col-md-2 d-flex align-items-end">
            <button
              className="custBtn"
              onClick={handleCertifiedUpload}
              disabled={!certifiedPdfFile || !(Number(pageInterval) > 0)}
              style={!certifiedPdfFile || !(Number(pageInterval) > 0) ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            >
              Upload Certified
            </button>
          </div>
          {error && (
            <div className="col-md-12 mt-2">
              <div style={{ color: "red", background: "#ffe6e6", padding: "10px", borderRadius: "5px", fontSize: "12px", whiteSpace: "pre-line" }}>
                {error}
              </div>
            </div>
          )}
        </div>
      )}

      {errorExcel && (
        <div className="row">
          <div className="col-md-12 d-flex justify-content-center ">
            <ClearForm
              message="Error In the File Uploaded Successfully!"
              redirectPath="/arbdashboard"
            />
          </div>
        </div>
      )}

      {clearForm && (
        <div className="row">
          <div className="col-md-12 d-flex justify-content-center ">
            <ClearForm
              message={doneMessage || "Certified Award Uploaded Successfully!"}
              redirectPath="/arbdashboard"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateAward;
